<script setup lang="ts">
import { useNavigation } from '@base/composables/navigation'
import type * as tp from '@base/utils/types'
import { ut_addPathToRoute, ut_getValuesByPath, ut_returnNewRoute } from '@base/utils/utils'
import { useAppIndexStore } from '@base/stores/indexStore'
import { ESpecialPage } from '@base/utils/constants'
import { useSettings } from '@base/composables/settings'
import { ut_wilo_isAllowlistedUser, ut_wilo_showPriceList } from '~/utils/utils'

type TmenuItem = { desc: string, subdesc?: string, link: string, external?: boolean, query?: Record<string, string>, invisible?: boolean }
type TmenuGroup = { caption: string, desc: string, items: Array<TmenuItem> }

const cp = useNavigation()
const indexStore = useAppIndexStore()

const p = defineProps<{
  isApplication: boolean
  isInternal: boolean
  realCountry: string
  showKoreanTeaser?: boolean
}>()

function linkPath(nodeName: string, slug: string): tp.IParamRouter {
  const route: tp.IParamRouter = ut_addPathToRoute(cp.linkPathBase.value, slug)
  // Modify to historic country
  if (nodeName === '8ae2819c8fb7d8dc018fb86834495b5d') {
    route.path = route.path.replace('/' + indexStore.state.paramData.rcco + '/', '/' + indexStore.state.paramData.rcco + '_h/')
  }
  return route
}

const internalMenu: Array<TmenuGroup> = [
  {
    caption: 'Product catalogues',
    desc: 'Product catalogues based on your local settings for language and country. Change language, country or frequency in your settings.',
    items: [
      {
        desc: 'Products',
        link: ['', indexStore.state.paramData.rcco, indexStore.state.paramData.lcda, 'root13', '8ae2819c8e9774cf018ea36fa2c86c6b'].join('/'),
        query: { rawurl: '1' },
      },
      {
        desc: 'Accessory',
        link: ['', indexStore.state.paramData.rcco, indexStore.state.paramData.lcda, 'root13', '8ae2819c8e9774cf018ea370051d1130'].join('/'),
        query: { rawurl: '1' },
      },
      {
        desc: 'Services',
        link: ['', indexStore.state.paramData.rcco, indexStore.state.paramData.lcda, 'root13', '8ae2819c8e9774cf018ea37790a356d0'].join('/'),
        query: { rawurl: '1' },
      },
      {
        desc: 'Motors',
        link: ['', indexStore.state.paramData.rcco, indexStore.state.paramData.lcda, 'root13', '8ae2819c8fb7d8dc018fb867e6d058a0'].join('/'),
        query: { rawurl: '1' },
      },
    ],
  },
  {
    caption: 'Specific catalogue variants',
    desc: 'Specific product catalogues based on preset settings. Your personal settings will be changed automatically.',
    items: [
      {
        desc: 'Products',
        subdesc: 'International 60 Hz',
        link: ['', '_international_60hz', 'en', 'root13', '8ae2819c8e9774cf018ea36fa2c86c6b'].join('/'),
        query: { rawurl: '1' },
      },
      {
        desc: 'Products',
        subdesc: 'OEM 50 Hz',
        link: ['', '_oem_hvac', 'en', 'root13', '8ae2819c8e9774cf018ea36fa2c86c6b'].join('/'),
        query: { rawurl: '1' },
      },
      {
        desc: 'Products',
        subdesc: 'International 50 Hz (incl. prices)',
        link: ['', '_international', 'en', 'root13', '8ae2819c8e9774cf018ea36fa2c86c6b'].join('/'),
        query: { rawurl: '1' },
      },
    ],
  },
  {
    caption: 'Sales & consulting tools',
    desc: 'Sales & consulting tools based on your settings for language and country. Change language, country or frequence in your settings.',
    items: [
      {
        desc: 'Replacement guide',
        link: ['', indexStore.state.paramData.rcco + '_h', indexStore.state.paramData.lcda, 'root13', '8ae2819c8fb7d8dc018fb86834495b5d'].join('/'),
        query: { rawurl: '1' },
      },
      {
        desc: 'Booklet creator',
        link: ['', indexStore.state.paramData.rcco, indexStore.state.paramData.lcda, 'root13', '8ae2819c8fb7d8dc018fb868b67b603e'].join('/'),
        query: { rawurl: '1' },
      },
      {
        desc: 'Digital product pass',
        link: ['', indexStore.state.paramData.rcco, indexStore.state.paramData.lcda, 'root13', '89e1809c9fe97b3e019ff0c74aaf2a4b'].join('/'),
        query: { rawurl: '1' },
        invisible: !ut_wilo_isAllowlistedUser(),
      },
      {
        desc: 'Product comparison',
        link: ut_returnNewRoute(indexStore.getRootPath + '/' + ESpecialPage.sp_product_compare, {}, '').path,
      },
      {
        desc: 'Catalogue creation',
        link: ut_returnNewRoute(indexStore.getRootPath + '/' + ESpecialPage.sp_catalogue_create, {}, '').path,
      },
      {
        desc: 'Price list creation',
        link: ut_returnNewRoute(indexStore.getRootPath + '/' + ESpecialPage.sp_price_list_create, {}, '').path,
        invisible: !ut_wilo_showPriceList(p.isInternal),
      },
      {
        desc: 'Downloads collection',
        link: ut_returnNewRoute(indexStore.getRootPath + '/' + ESpecialPage.sp_download_list, {}, '').path,
      },
      {
        desc: 'Wilo-Select<sup>*</sup>',
        link: 'https://select-int.wilo.com ',
        external: true,
      },
    ],
  },
  {
    caption: 'Quick dimensioning & Solution finder',
    desc: '',
    items: [
      {
        desc: 'Circulation pumps',
        subdesc: 'for Building Service Residential',
        link: ['', indexStore.state.paramData.rcco, indexStore.state.paramData.lcda, 'root13', '8ae2819c8fb7d8dc018fb86872e95d8f'].join('/'),
        query: { rawurl: '1' },
      },
      {
        desc: 'Water Quality Systems',
        link: 'https://resmktgwqsfrontendapp1prod.azurewebsites.net/?lang=%lcda&market=%rcco',
        external: true,
      },
      {
        desc: 'Rainwater Utilisation Systems',
        link: 'https://resmktgesfrontendapp1prod.azurewebsites.net/rwus?lang=%lcda&market=%rcco',
        external: true,
      },
      {
        desc: 'Easy Select',
        subdesc: 'Wastewater lifting units',
        link: 'https://resmktgesfrontendapp1prod.azurewebsites.net/sew?lang=%lcda&market=%rcco',
        external: true,
      },
    ],
  },
  {
    caption: 'Marketing related tools',
    desc: 'Further tools not referring to your settings',
    items: [
      {
        desc: 'Wilo-World',
        external: true,
        link: 'https://wilo.com/en/Wilo-World.html',
      },
      {
        desc: 'Wilo Media Database',
        external: true,
        link: 'https://mediadatabase.wilo.com/',
      },
      {
        desc: 'Wilo Reference Database',
        external: true,
        link: 'https://references.ads.wilo.de/',
      },
      {
        desc: 'Wilo Translation Portal',
        external: true,
        link: 'https://wilo-ctp.gemino.de/',
      },
      {
        desc: 'Wilo Terminology Database',
        external: true,
        link: 'https://wilo-terminology-db.ads.wilo.de/CongreeControlCenter/TermTiger/search',
      },
    ],
  },
]
</script>

<template>
  <UContainer>
    <template v-if="cp.navigationData.value">
      <!-- Internal -->
      <template v-if="isInternal">
        <h2 class="text-center w-full font-bold" v-html="'Internal product and solution finder'" />
        <div v-for="(group, gindex) in internalMenu" :key="gindex" class="mt-16">
          <h2 v-html="group.caption" />
          <div class="mt-2 mb-6" v-html="group.desc" />
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <template v-for="(item, index) in group.items" :key="index">
              <NuxtLink
                v-if="!item.invisible"
                :to="!item.external ? { path: item.link, query: item.query || {} } : ''"
                :href="item.external ? item.link.replace('%lcda', cp.indexStore.state.paramData.lcda).replace('%rcco', cp.indexStore.state.paramData.rcco) : ''"
                :target="item.external ? '_blank' : '_self'"
              >
                <UCard variant="solid">
                  <div class="p-8">
                    <h4 class="text-center" v-html="item.desc" />
                    <div v-if="item.subdesc" class="text-center" v-html="item.subdesc" />
                  </div>
                </UCard>
              </NuxtLink>
            </template>
          </div>
          <template v-if="gindex === 2">
            <div class="mt-4" v-html="'<sup>*</sup>not referring to your settings'" />
          </template>
        </div>
        <div class="mt-16 md:flex justify-between">
          <div v-html="'Updated version from ' + (useSettings().sysInfo.value?.productionDate || '').split('T')[0]" />
          <UButton class="mt-4 md:mt-0" variant="ghost" color="tertiary" icon="i-wilo-chevron-right-solid-full" label="Release notes" target="_blank" href="https://wilogroup.sharepoint.com/:u:/r/sites/003166/SitePages/Online-Catalogue.aspx?csf=1&web=1&e=SFyBJZ" />
        </div>
      </template>
      <!-- Not Internal -->
      <template v-else>
        <div v-if="isApplication" class="mb-4 flex flex-wrap justify-between -mx-2">
          <div v-html="ut_getValuesByPath(cp.indexStore.state.currentNode, '8ae2889e5861001c01586d69d8e96ff6')" />  <!-- desc_generall --> <!-- Application intro -->
          <LazyLinkTeaser v-if="showKoreanTeaser" :rcco="indexStore.state.paramData.rcco" />
          <SettingsSmall :is-historic="false" :real-country="realCountry" :is-internal="isInternal" :show-frequency="true" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <template v-for="item in cp.navigationData.value" :key="item.nodeName">
            <template v-if="!(item.attrs && item.attrs.FAT === '1')">
              <!-- Application cards -->
              <UCard v-if="isApplication" variant="solid">
                <template #header>
                  <NuxtLink :to="linkPath(item.nodeName, item.meta?.slug || '')">
                    <Image
                      v-if="ut_getValuesByPath(item, 'graphic_data_aerial_view')"
                      :src="ut_getValuesByPath(item, 'graphic_data_aerial_view')"
                      :alt="item.desc"
                    />
                  </NuxtLink>
                </template>
                <NuxtLink :to="linkPath(item.nodeName, item.meta?.slug || '')">
                  <div class="p-8">
                    <h4 v-html="item.desc" />
                    <div class="mt-2" v-html="ut_getValuesByPath(item, '8ae2819c60d3f11d0160e55edb052112')" />  <!-- promotional_subline -->
                    <UButton class="mt-8" icon="i-wilo-chevron-right-solid-full" :label="item.desc" />
                  </div>
                </NuxtLink>
              </UCard>
              <!-- Root cards -->
              <UCard v-else variant="solid">
                <NuxtLink :to="linkPath(item.nodeName, item.meta?.slug || '')">
                  <div class="p-8">
                    <h4 class="text-center" v-html="item.desc" />
                  </div>
                </NuxtLink>
              </UCard>
            </template>
          </template>
        </div>
      </template>
    </template>
  </UContainer>
</template>
